import { useEffect, useState } from "react";
import { useIncidentActions } from "@/hooks/useIncidentActions";
import { AttachmentsGallery } from "@/components/AttachmentsGallery";
import { IncidentActionBar } from "@/components/incident/IncidentActionBar";
import { IncidentMetaGrid } from "@/components/incident/IncidentMetaGrid";
import { IncidentHeader } from "@/components/incident/IncidentHeader";
import { SynopsisPanel } from "@/components/incident/SynopsisPanel";
import { Save, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const IncidentDetail = ({ incident, onUpdated, onDeleted }) => {
  const [draft, setDraft] = useState(incident);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(incident);
    setDirty(false);
  }, [incident]);

  const set = (k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  };

  const { saving, downloading, save, remove, exportPdf } =
    useIncidentActions({ incident, draft, onUpdated, onDeleted, setDirty });

  return (
    <div className="flex flex-col h-full">
      <IncidentActionBar draft={draft} downloading={downloading} onExportPdf={exportPdf} onDelete={remove} />

      <div className="flex-1 overflow-y-auto scroll-thin px-6 py-6 space-y-8">
        <IncidentHeader draft={draft} onSet={set} onTitleGenerated={(data) => {
          setDraft((d) => ({ ...d, title: data.title }));
          onUpdated(data);
        }} />

        <SynopsisPanel incident={draft} onSynopsis={(data) => {
          setDraft((d) => ({ ...d, synopsis: data.synopsis, synopsis_generated_at: data.synopsis_generated_at }));
          onUpdated(data);
        }} />

        <IncidentMetaGrid draft={draft} onSet={set} />

        {/* description */}
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
            <FileText className="w-3.5 h-3.5" /> Description
          </label>
          <Textarea value={draft.description} onChange={(e) => set("description", e.target.value)} data-testid="description-input"
            rows={3} className="bg-[#0A0A0A] border-border rounded-sm resize-none focus-visible:ring-[#007AFF]" />
        </div>

        <AttachmentsGallery incident={draft}
          onUpdated={(data) => { setDraft((d) => ({ ...d, attachments: data.attachments })); onUpdated(data); }} />

        {/* transcript */}
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
            <FileText className="w-3.5 h-3.5" /> Transcript <span className="text-[#007AFF] normal-case tracking-normal font-normal">(editable)</span>
          </label>
          <Textarea value={draft.transcript} onChange={(e) => set("transcript", e.target.value)} data-testid="transcript-edit-textarea"
            rows={14} className="bg-[#0A0A0A] border-border rounded-sm font-mono text-sm leading-relaxed resize-y focus-visible:ring-[#007AFF]" />
        </div>
      </div>

      {/* sticky save */}
      <div className="border-t border-border px-6 py-3 flex items-center justify-between bg-background">
        <span className="text-xs text-muted-foreground">{dirty ? "Unsaved changes" : "All changes saved"}</span>
        <Button onClick={save} disabled={!dirty || saving} data-testid="save-incident-btn"
          className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};
